import React, { useState, useEffect } from "react";
import { API } from "../../App";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";

const COST = [
  { value: "increase", label: "Increase" },
  { value: "decrease", label: "Decrease" },
  { value: "no_change", label: "No Change" },
  { value: "tbc", label: "TBC" },
];
const TIME = [
  { value: "extension", label: "Extension" },
  { value: "reduction", label: "Reduction" },
  { value: "no_change", label: "No Change" },
  { value: "tbc", label: "TBC" },
];

const today = () => new Date().toISOString().slice(0, 10);

const empty = () => ({
  conversation_id: "__none__",
  client_name: "",
  client_email: "",
  client_phone: "",
  date_raised: today(),
  raised_by: "builder",
  description: "",
  cost_impact: "tbc",
  cost_amount: "",
  timeline_impact: "tbc",
  timeline_days: "",
  builder_notes: "",
});

function Pills({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2 mt-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
            value === o.value
              ? "bg-amber-600 text-white border-amber-600"
              : "bg-white text-slate-600 border-slate-300 hover:border-amber-400"
          }`}
          data-testid={`${testidPrefix}-${o.value}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function VariationFormDialog({
  open,
  onClose,
  siteId,
  editRecord,
  conversations,
  defaultClient,
  onSaved,
}) {
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const token = () => localStorage.getItem("token");

  useEffect(() => {
    if (open) {
      setForm(
        editRecord
          ? {
              conversation_id: editRecord.conversation_id || "__none__",
              client_name: editRecord.client_name || "",
              client_email: editRecord.client_email || "",
              client_phone: editRecord.client_phone || "",
              date_raised: editRecord.date_raised || today(),
              raised_by: editRecord.raised_by || "builder",
              description: editRecord.description || "",
              cost_impact: editRecord.cost_impact || "tbc",
              cost_amount:
                editRecord.cost_amount != null ? String(editRecord.cost_amount) : "",
              timeline_impact: editRecord.timeline_impact || "tbc",
              timeline_days:
                editRecord.timeline_days != null ? String(editRecord.timeline_days) : "",
              builder_notes: editRecord.builder_notes || "",
            }
          : {
              ...empty(),
              client_name: defaultClient?.client_name || "",
              client_email: defaultClient?.client_email || "",
              client_phone: defaultClient?.client_phone || "",
            }
      );
    }
  }, [open, editRecord, defaultClient]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onPickConversation = (id) => {
    set("conversation_id", id);
    if (id !== "__none__") {
      const c = conversations.find((x) => x.id === id);
      if (c) {
        setForm((f) => ({
          ...f,
          conversation_id: id,
          client_name: c.client_name,
          client_email: c.client_email,
          client_phone: c.client_phone || "",
        }));
      }
    }
  };

  const save = async () => {
    if (!form.client_name.trim() || !form.client_email.trim()) {
      toast.error("Client name and email are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        conversation_id:
          form.conversation_id === "__none__" ? null : form.conversation_id,
        client_name: form.client_name,
        client_email: form.client_email,
        client_phone: form.client_phone,
        date_raised: form.date_raised,
        raised_by: form.raised_by,
        description: form.description,
        cost_impact: form.cost_impact,
        cost_amount:
          form.cost_impact === "increase" || form.cost_impact === "decrease"
            ? form.cost_amount === ""
              ? null
              : Number(form.cost_amount)
            : null,
        timeline_impact: form.timeline_impact,
        timeline_days:
          form.timeline_impact === "extension" || form.timeline_impact === "reduction"
            ? form.timeline_days === ""
              ? null
              : Number(form.timeline_days)
            : null,
        builder_notes: form.builder_notes,
      };
      const url = editRecord
        ? `${API}/client-variations/${editRecord.id}`
        : `${API}/client-variations`;
      const method = editRecord ? "PUT" : "POST";
      const body = editRecord ? payload : { ...payload, site_id: siteId };
      const res = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${token()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || "Save failed");
      }
      toast.success(editRecord ? "Variation updated" : "Variation logged");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const showCost = form.cost_impact === "increase" || form.cost_impact === "decrease";
  const showTime =
    form.timeline_impact === "extension" || form.timeline_impact === "reduction";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="variation-form-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {editRecord
              ? `Edit Variation ${editRecord.variation_number}`
              : "Log Variation"}
          </DialogTitle>
          <DialogDescription>
            Saved as a draft — the variation number is assigned automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label>Link to conversation (optional)</Label>
            <Select value={form.conversation_id} onValueChange={onPickConversation}>
              <SelectTrigger data-testid="variation-conversation-select">
                <SelectValue placeholder="None" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">None</SelectItem>
                {conversations.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.date} · {c.subject || c.client_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client name *</Label>
              <Input
                value={form.client_name}
                onChange={(e) => set("client_name", e.target.value)}
                data-testid="variation-client-name"
              />
            </div>
            <div>
              <Label>Client email *</Label>
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => set("client_email", e.target.value)}
                data-testid="variation-client-email"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client phone</Label>
              <Input
                value={form.client_phone}
                onChange={(e) => set("client_phone", e.target.value)}
                data-testid="variation-client-phone"
              />
            </div>
            <div>
              <Label>Date raised</Label>
              <Input
                type="date"
                value={form.date_raised}
                onChange={(e) => set("date_raised", e.target.value)}
                data-testid="variation-date"
              />
            </div>
          </div>

          <div>
            <Label>Raised by</Label>
            <Pills
              options={[
                { value: "client", label: "Client" },
                { value: "builder", label: "Builder" },
              ]}
              value={form.raised_by}
              onChange={(v) => set("raised_by", v)}
              testidPrefix="variation-raisedby"
            />
          </div>

          {editRecord && (
            <p className="text-sm text-slate-500">
              Variation number:{" "}
              <span className="font-semibold text-slate-800">
                {editRecord.variation_number}
              </span>
            </p>
          )}

          <div>
            <Label>Description</Label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Full detail of what the variation involves"
              data-testid="variation-description"
            />
          </div>

          <div>
            <Label>Cost impact</Label>
            <Pills
              options={COST}
              value={form.cost_impact}
              onChange={(v) => set("cost_impact", v)}
              testidPrefix="variation-cost"
            />
          </div>
          {showCost && (
            <div>
              <Label>Cost amount ($)</Label>
              <Input
                type="number"
                value={form.cost_amount}
                onChange={(e) => set("cost_amount", e.target.value)}
                data-testid="variation-cost-amount"
              />
            </div>
          )}

          <div>
            <Label>Timeline impact</Label>
            <Pills
              options={TIME}
              value={form.timeline_impact}
              onChange={(v) => set("timeline_impact", v)}
              testidPrefix="variation-time"
            />
          </div>
          {showTime && (
            <div>
              <Label>Timeline days</Label>
              <Input
                type="number"
                value={form.timeline_days}
                onChange={(e) => set("timeline_days", e.target.value)}
                data-testid="variation-timeline-days"
              />
            </div>
          )}

          <div>
            <Label className="text-rose-600">
              Builder notes — internal only, not shown to client
            </Label>
            <Textarea
              rows={3}
              value={form.builder_notes}
              onChange={(e) => set("builder_notes", e.target.value)}
              data-testid="variation-builder-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving} data-testid="variation-save-btn">
            {saving ? "Saving..." : "Save as Draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
